import {
  type V2AdminInventoryCommentDto,
  v2AdminInventoryCommentsControllerCreate,
  v2AdminInventoryCommentsControllerList,
  v2AdminInventoryCommentsControllerRemove
} from '@idc-forms/api-client'
import { V2AdminInventoryCommentsSchema } from '@idc-forms/schemas/api-v2/admin-inventory-comments'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * El comentario tal como lo entrega el SDK.
 *
 * Se tipa contra el DTO GENERADO y no contra el tipo de zod a propósito: el schema del servidor
 * marca los ids públicos (`icm_`, `usr_`) y el SDK los aplana a `string`. Adoptar el tipo branded
 * acá obligaría a una aserción que sólo fingiría una garantía que el wire no da.
 */
export type InventoryCommentV2 = V2AdminInventoryCommentDto

/**
 * Comentarios de inventario contra la API v2.
 *
 * Todo se direcciona por id público: el inmueble por `prp_` y el comentario por `icm_`. Nótese que
 * el borrado v2 pide LOS DOS —a diferencia de v1, que borraba con sólo el id del comentario—.
 * No es burocracia: es lo que deja al servidor comprobar que el comentario pertenece al inmueble
 * que el que llama dice estar mirando, en vez de confiar en un id suelto.
 *
 * El servidor decide quién puede borrar (el autor lo suyo, el moderador cualquiera); acá no se
 * replica esa regla, sólo se dibuja el botón según `isOwn` del view model.
 */
export const inventoryCommentsV2Service = {
  list: async (accessToken: string, propertyId: string): Promise<InventoryCommentV2[]> => {
    const result = await v2AdminInventoryCommentsControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { propertyId }
    })
    return assertShape(V2AdminInventoryCommentsSchema, result.data).data
  },

  create: async (
    accessToken: string,
    propertyId: string,
    input: { body: string; parent?: string }
  ): Promise<InventoryCommentV2> => {
    const result = await v2AdminInventoryCommentsControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { propertyId },
      body: input as never
    })
    return result.data
  },

  remove: async (accessToken: string, propertyId: string, id: string): Promise<void> => {
    await v2AdminInventoryCommentsControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { propertyId, id }
    })
  }
}
